import React, { useState, useEffect, useRef, useCallback, useMemo, memo, lazy, Suspense } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from '@mui/material';
import {
  WalteroCard,
  WalteroButton,
  WalteroStatusChip,
  WalteroTypography,
  WalteroSkeleton
} from './waltero';
import {
  Send as SendIcon,
  SmartToy as AgentIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Add as AddIcon,
  Chat as ChatIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import agenticService from '../services/agenticService';
import { useModalFocus } from '../hooks/useFocusManagement';
import { useResponsive } from '../hooks/useResponsive';

// Memoized message list component to prevent unnecessary re-renders
const MessageList = memo(({ messages, renderMessage }) => {
  return (
    <>
      {messages.map(renderMessage)}
    </>
  );
});

MessageList.displayName = 'MessageList';
// Removed CSS import - using Waltero design system

const AgentTab = () => {
  const theme = useTheme();
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [error, setError] = useState('');
  
  // Conversation management state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showConversations, setShowConversations] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  
  // Polling state
  const [isPolling, setIsPolling] = useState(false);
  const [pollingStatus, setPollingStatus] = useState('');
  const [pollingProgress, setPollingProgress] = useState(0);
  
  // UI state
  const [healthStatus, setHealthStatus] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mainContainerRef = useRef(null);
  
  // Responsive configuration
  const responsive = useResponsive();
  const { breakpoints, device, grid, touch } = responsive;
  
  // Focus management for modals
  const conversationsFocus = useModalFocus(showConversations, {
    initialFocusSelector: '[role="listbox"] [role="option"]:first-child'
  });
  const infoFocus = useModalFocus(showInfo, {
    initialFocusSelector: 'button[aria-label="Close dialog"]'
  });

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check health status and load conversations on component mount
  useEffect(() => {
    checkHealthStatus();
    loadConversations();
  }, []);

  const checkHealthStatus = async () => {
    try {
      const health = await agenticService.checkHealth();
      setHealthStatus(health);
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthStatus({ status: 'ERROR', message: error.message });
    }
  };

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const response = await agenticService.getUserThreads(20);
      setConversations(response.threads || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setCurrentThreadId(null);
    setSelectedConversation(null);
    setError('');
    setShowConversations(false);
    agenticService.cancelAllPolling();
  };

  const selectConversation = async (conversation) => {
    try {
      setError('');
      setIsLoading(true);
      setSelectedConversation(conversation);
      setCurrentThreadId(conversation.threadId);
      
      // Load conversation messages
      const response = await agenticService.getThreadMessages(conversation.threadId);
      
      // Transform messages to the expected format
      const loadedMessages = [];
      
      response.messages.forEach(msg => {
        // Add user message
        loadedMessages.push({
          id: `${msg.id}_user`,
          content: msg.content,
          role: 'user',
          timestamp: msg.timestamp
        });
        
        // Add AI response if available
        if (msg.response) {
          let messageContent;
          
          if (typeof msg.response === 'string') {
            messageContent = msg.response;
          } else if (msg.response.message) {
            // Extract the actual message content from the response object
            messageContent = msg.response.message;
          } else {
            // Fallback to stringifying if we can't find the message field
            messageContent = JSON.stringify(msg.response, null, 2);
          }
          
          loadedMessages.push({
            id: `${msg.id}_assistant`,
            content: messageContent,
            role: 'assistant',
            timestamp: msg.timestamp
          });
        }
      });
      
      setMessages(loadedMessages);
      setShowConversations(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      content: inputMessage.trim(),
      role: 'user',
      timestamp: new Date().toISOString()
    };

    // Add user message to chat
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setError('');
    setIsLoading(true);

    try {
      // Start chat with AI Assistant
      const chatResponse = await agenticService.startChat(userMessage.content, currentThreadId);
      console.log('🚀 Chat started:', chatResponse);

      // Start polling for response
      setIsPolling(true);
      setPollingStatus('Waiting for AI response...');
      setPollingProgress(0);

      const finalResponse = await agenticService.pollForResponse(
        chatResponse.requestId,
        (update) => {
          // Use detailed status if available, fallback to generic message
          let statusMessage = `Processing... (${update.attempt}/${update.maxAttempts})`;
          
          if (update.statusDetails) {
            const { stage, message } = update.statusDetails;
            const stageIcons = {
              'initializing': '🔄',
              'agent_working': '🤖',
              'searching_data': '🔍',
              'preparing_response': '✍️',
              'completed': '✅'
            };
            
            const icon = stageIcons[stage] || '⚙️';
            statusMessage = `${icon} ${message}`;
            
            // Add function call details if available
            if (update.statusDetails.functionCalls && update.statusDetails.functionCalls.length > 0) {
              statusMessage += ` (${update.statusDetails.functionCalls.join(', ')})`;
            }
          }
          
          setPollingStatus(statusMessage);
          setPollingProgress((update.attempt / update.maxAttempts) * 100);
        }
      );

      // Add AI response to chat
      console.log('🎯 Final response received:', finalResponse);
      let responseMessage = finalResponse.response?.message || finalResponse.response || 'No response received';
      
      // Ensure message is always a string (in case we get an object)
      if (typeof responseMessage !== 'string') {
        console.warn('⚠️ Response message is not a string, converting:', responseMessage);
        responseMessage = JSON.stringify(responseMessage, null, 2);
      }
      
      console.log('📝 Extracted message:', responseMessage);
      
      // Update thread ID with the real OpenAI thread ID from the final response
      const realThreadId = finalResponse.response?.threadId || finalResponse.threadId;
      if (realThreadId && realThreadId !== currentThreadId) {
        console.log(`🔄 Updating thread ID from final response: ${currentThreadId} -> ${realThreadId}`);
        setCurrentThreadId(realThreadId);
      }
      
      const aiMessage = {
        id: Date.now() + 1,
        content: responseMessage,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        functionCalls: agenticService.parseFunctionCalls(responseMessage),
        metadata: {
          threadId: realThreadId,
          processingTime: finalResponse.processingTime,
          responseType: finalResponse.response?.type
        }
      };

      console.log('💬 Adding AI message to chat:', aiMessage);

      setMessages(prev => [...prev, aiMessage]);
      
      // Reload conversations to include any new thread
      loadConversations();
      
    } catch (error) {
      console.error('Chat error:', error);
      setError(error.message);
      
      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        content: `Error: ${error.message}`,
        role: 'error',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsPolling(false);
      setPollingStatus('');
      setPollingProgress(0);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Enhanced keyboard navigation for main interface
  const handleMainKeyDown = useCallback((event) => {
    const { key, ctrlKey, metaKey, altKey } = event;
    
    // Keyboard shortcuts for main interface
    if ((ctrlKey || metaKey) && !altKey) {
      switch (key) {
        case 'n':
          event.preventDefault();
          startNewConversation();
          break;
        case 'h':
          event.preventDefault();
          setShowInfo(true);
          break;
        case 'l':
          event.preventDefault();
          setShowConversations(true);
          break;
        case '/':
          event.preventDefault();
          inputRef.current?.focus();
          break;
        default:
          break;
      }
    }
    
    // Escape key handling
    if (key === 'Escape' && !showConversations && !showInfo) {
      if (document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      } else {
        clearChat();
      }
    }
  }, [showConversations, showInfo]);
  
  // Memoize expensive calculations
  const conversationStats = useMemo(() => ({
    count: conversations.length,
    hasActive: selectedConversation !== null,
    activeTitle: selectedConversation?.title,
    activeThreadId: selectedConversation?.threadId
  }), [conversations, selectedConversation]);
  
  const messageStats = useMemo(() => ({
    total: messages.length,
    userMessages: messages.filter(m => m.role === 'user').length,
    assistantMessages: messages.filter(m => m.role === 'assistant').length,
    errorMessages: messages.filter(m => m.role === 'error').length
  }), [messages]);
  
  // Add keyboard event listeners
  useEffect(() => {
    const container = mainContainerRef.current;
    if (container) {
      container.addEventListener('keydown', handleMainKeyDown);
      return () => container.removeEventListener('keydown', handleMainKeyDown);
    }
  }, [handleMainKeyDown]);

  const clearChat = () => {
    startNewConversation();
  };

  const formatTimestamp = useCallback((timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  }, []);

  const getMessageIcon = useCallback((role) => {
    switch (role) {
      case 'user':
        return <PersonIcon />;
      case 'assistant':
        return <AgentIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  }, []);

  const getMessageColor = useCallback((role) => {
    switch (role) {
      case 'user':
        return 'primary';
      case 'assistant':
        return 'secondary';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  }, []);

  const renderMessage = useCallback((message) => {
    const getMessageVariant = (role) => {
      switch (role) {
        case 'user':
          return 'elevated';
        case 'error':
          return 'outlined';
        default:
          return 'default';
      }
    };

    const getMessageBgColor = (role) => {
      switch (role) {
        case 'user':
          return theme.palette.waltero.primary.lightBlue;
        case 'error':
          return theme.palette.waltero.secondary.backgroundApricot;
        default:
          return theme.palette.waltero.neutral;
      }
    };

    return (
      <WalteroCard
        key={message.id}
        variant={getMessageVariant(message.role)}
        sx={{
          mb: 2,
          mr: message.role === 'user' ? 0 : 4,
          ml: message.role === 'user' ? 4 : 0,
          bgcolor: getMessageBgColor(message.role)
        }}
        role="article"
        aria-label={`Message from ${message.role === 'user' ? 'you' : message.role === 'assistant' ? 'AI Assistant' : 'system'}`}
        tabIndex={0}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: getMessageColor(message.role) + '.main' 
              }}
            >
              {getMessageIcon(message.role)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <WalteroTypography variant="subtitle2" color="secondary">
                  {message.role === 'user' ? 'You' : 
                   message.role === 'assistant' ? 'AI Assistant' : 'System'}
                </WalteroTypography>
                <WalteroTypography variant="caption" color="secondary">
                  {formatTimestamp(message.timestamp)}
                </WalteroTypography>
              </Box>
              <WalteroTypography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: message.role === 'user' ? 'white' : 'inherit' }}>
                {message.content}
              </WalteroTypography>
              {message.functionCalls && message.functionCalls.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {message.functionCalls.map((call, index) => (
                    <WalteroStatusChip 
                      key={index}
                      label={`Function: ${call.function}`}
                      status="info"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </WalteroCard>
    );
  }, [getMessageIcon, getMessageColor, formatTimestamp, theme.palette]);

  return (
    <Box 
      ref={mainContainerRef}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      role="main"
      aria-label="AI Assistant Chat Interface"
      tabIndex={-1}
    >
      {/* Header */}
      <WalteroCard variant="accent" sx={{ mb: grid.container.spacing }} role="banner">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: breakpoints.isMobile ? 1.5 : 2,
          flexDirection: breakpoints.isSmallMobile ? 'column' : 'row',
          gap: breakpoints.isSmallMobile ? 1 : 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AgentIcon sx={{ color: 'white' }} aria-hidden="true" />
            <WalteroTypography 
              variant={breakpoints.isMobile ? "h5" : "h4"} 
              color="white" 
              component="h1"
              sx={{ fontSize: breakpoints.isSmallMobile ? '1.25rem' : undefined }}
            >
              AI Assistant
            </WalteroTypography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }} role="toolbar" aria-label="Assistant actions">
            <Tooltip title="Conversations (Ctrl+L)">
              <IconButton
                color="inherit"
                onClick={() => setShowConversations(true)}
                size="small"
                aria-label="Open conversations list"
                sx={{ minWidth: '44px', minHeight: '44px' }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="New Conversation (Ctrl+N)">
              <IconButton
                color="inherit"
                onClick={startNewConversation}
                size="small"
                disabled={isLoading}
                aria-label="Start new conversation"
                sx={{ minWidth: '44px', minHeight: '44px' }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Service Health (Ctrl+H)">
              <IconButton
                color="inherit"
                onClick={() => setShowInfo(true)}
                size="small"
                aria-label={`Service health: ${healthStatus?.status || 'Unknown'}`}
                sx={{ minWidth: '44px', minHeight: '44px' }}
              >
                {healthStatus?.status === 'OK' ? 
                  <SuccessIcon color="success" aria-label="Service healthy" /> : 
                  <ErrorIcon color="error" aria-label="Service error" />
                }
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                color="inherit"
                onClick={checkHealthStatus}
                size="small"
                aria-label="Refresh service health status"
                sx={{ minWidth: '44px', minHeight: '44px' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear Chat">
              <IconButton
                color="inherit"
                onClick={clearChat}
                size="small"
                disabled={isLoading}
                aria-label="Clear current conversation"
                sx={{ minWidth: '44px', minHeight: '44px' }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Status indicators */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Box>
            {selectedConversation && (
              <WalteroTypography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                📝 {selectedConversation.title}
              </WalteroTypography>
            )}
            {!selectedConversation && currentThreadId && (
              <WalteroTypography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                Thread: {currentThreadId.substring(0, 8)}...
              </WalteroTypography>
            )}
          </Box>
          {conversationStats.count > 0 && (
            <WalteroStatusChip 
              status="info" 
              label={`${conversationStats.count} conversation${conversationStats.count !== 1 ? 's' : ''}`}
              size="small"
              aria-label={`${conversationStats.count} total conversations available`}
            />
          )}
        </Box>
      </WalteroCard>

      {/* Error display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Polling status */}
      {isPolling && (
        <WalteroCard variant="outlined" sx={{ mb: 2, bgcolor: theme.palette.waltero.secondary.backgroundApricot }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, p: 2 }}>
            <CircularProgress size={20} sx={{ color: theme.palette.waltero.secondary.primaryApricot }} />
            <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', color: theme.palette.waltero.primary.coreBlue }}>
              {pollingStatus}
            </WalteroTypography>
          </Box>
          
          {/* Stage progress indicators */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
            {['initializing', 'agent_working', 'searching_data', 'preparing_response', 'completed'].map((stage, index) => {
              const stageLabels = {
                'initializing': 'Starting',
                'agent_working': 'Analyzing',
                'searching_data': 'Searching',
                'preparing_response': 'Preparing',
                'completed': 'Done'
              };
              
              const stageIcons = {
                'initializing': '🔄',
                'agent_working': '🤖',
                'searching_data': '🔍',
                'preparing_response': '✍️',
                'completed': '✅'
              };
              
              // Determine if this stage is active based on current polling status
              const isActive = pollingStatus.includes(stageIcons[stage]) || 
                               (stage === 'completed' && pollingStatus.includes('✅'));
              
              return (
                <Box 
                  key={stage}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    opacity: isActive ? 1 : 0.4,
                    transition: 'opacity 0.3s ease',
                    minWidth: '60px'
                  }}
                >
                  <WalteroTypography variant="h6" sx={{ mb: 0.5 }}>
                    {stageIcons[stage]}
                  </WalteroTypography>
                  <WalteroTypography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                    {stageLabels[stage]}
                  </WalteroTypography>
                </Box>
              );
            })}
          </Box>
          
          {pollingProgress > 0 && (
            <LinearProgress 
              variant="determinate" 
              value={pollingProgress} 
              sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.3)' }}
              color="inherit"
            />
          )}
        </WalteroCard>
      )}

      {/* Chat messages */}
      <WalteroCard
        variant="outlined" 
        sx={{ 
          flexGrow: 1, 
          p: 2, 
          overflow: 'auto',
          maxHeight: 'calc(100vh - 300px)',
          mb: 2
        }}
        role="log"
        aria-label="Chat conversation"
        aria-live="polite"
        aria-atomic="false"
      >
        {messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <AgentIcon sx={{ fontSize: 64, mb: 2, color: theme.palette.waltero.primary.coreBlue, opacity: 0.5 }} />
            <WalteroTypography variant="h5" sx={{ mb: 2 }}>Welcome to the AI Assistant</WalteroTypography>
            <WalteroTypography variant="body1" color="secondary">
              Ask me about your IoT devices, sensor data, or get help with device management.
              I can look up devices by serial numbers, query sensor telemetry, and check device configurations.
            </WalteroTypography>
            <Box sx={{ mt: 3 }}>
              <WalteroTypography variant="caption" color="secondary">
                Example questions:
              </WalteroTypography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary={<WalteroTypography variant="caption" color="secondary">Show me temperature data for device 12345 over the last 24 hours</WalteroTypography>}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary={<WalteroTypography variant="caption" color="secondary">What's the battery status of devices 11111, 22222?</WalteroTypography>}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary={<WalteroTypography variant="caption" color="secondary">Check the WiFi configuration for device ABC123</WalteroTypography>}
                  />
                </ListItem>
              </List>
            </Box>
          </Box>
        ) : (
          <>
            <MessageList messages={messages} renderMessage={renderMessage} />
            <div ref={messagesEndRef} />
          </>
        )}
      </WalteroCard>

      {/* Input area */}
      <WalteroCard variant="outlined" sx={{ p: 2 }} role="form" aria-label="Message input">
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            ref={inputRef}
            fullWidth
            variant="outlined"
            placeholder="Ask me about your IoT devices... (Press Ctrl+/ to focus)"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            multiline
            maxRows={3}
            aria-label="Message input"
            aria-describedby="input-help-text"
          />
          <WalteroButton
            variant="primary"
            endIcon={isLoading ? null : <SendIcon />}
            onClick={handleSendMessage}
            disabled={isLoading || !inputMessage.trim()}
            sx={{ minWidth: 100, minHeight: '44px' }}
            aria-label={isLoading ? 'Sending message' : 'Send message'}
          >
            {isLoading ? <CircularProgress size={20} /> : 'SEND'}
          </WalteroButton>
        </Box>
        <WalteroTypography 
          id="input-help-text" 
          variant="caption" 
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Press Enter to send, Shift+Enter for new line. Keyboard shortcuts: Ctrl+N (new), Ctrl+L (conversations), Ctrl+H (help)
        </WalteroTypography>
      </WalteroCard>

      {/* Conversations Dialog */}
      <Dialog
        open={showConversations}
        onClose={() => setShowConversations(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="conversations-title"
        aria-describedby="conversations-description"
        ref={conversationsFocus.containerRef}
        onKeyDown={conversationsFocus.handleKeyDown}
      >
        <DialogTitle id="conversations-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon aria-hidden="true" />
            Conversations
          </Box>
        </DialogTitle>
        <DialogContent id="conversations-description">
          {loadingConversations ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 4 }} aria-live="polite">
              <WalteroSkeleton width="100%" height={60} />
              <WalteroSkeleton width="100%" height={60} />
              <WalteroSkeleton width="100%" height={60} />
              <WalteroTypography variant="body2" color="secondary" sx={{ textAlign: 'center' }}>
                Loading conversations...
              </WalteroTypography>
            </Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
              <ChatIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} aria-hidden="true" />
              <WalteroTypography variant="h6" color="secondary">No conversations yet</WalteroTypography>
              <WalteroTypography variant="body2" color="secondary">
                Start a new conversation to begin chatting with the AI assistant.
              </WalteroTypography>
            </Box>
          ) : (
            <List 
              role="listbox" 
              aria-label="Conversation list"
              aria-describedby="conversations-help"
            >
              <WalteroTypography 
                id="conversations-help" 
                variant="caption" 
                color="text.secondary"
                sx={{ px: 2, pb: 1, display: 'block' }}
              >
                Select a conversation to continue. Use arrow keys to navigate, Enter to select.
              </WalteroTypography>
              {conversations.map((conversation, index) => (
                <React.Fragment key={conversation.threadId}>
                  <ListItem
                    button
                    role="option"
                    onClick={() => selectConversation(conversation)}
                    selected={selectedConversation?.threadId === conversation.threadId}
                    aria-selected={selectedConversation?.threadId === conversation.threadId}
                    aria-describedby={`conversation-${index}-description`}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      minHeight: '44px',
                      bgcolor: selectedConversation?.threadId === conversation.threadId ? 'action.selected' : 'transparent'
                    }}
                    tabIndex={0}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <WalteroTypography variant="subtitle1" noWrap color="primary">
                        {conversation.title}
                      </WalteroTypography>
                      <WalteroTypography 
                        id={`conversation-${index}-description`}
                        variant="body2" 
                        color="secondary" 
                        noWrap
                      >
                        {conversation.lastMessage}
                      </WalteroTypography>
                      <WalteroTypography variant="caption" color="secondary">
                        {new Date(conversation.lastActivity).toLocaleString()}
                      </WalteroTypography>
                    </Box>
                    <WalteroStatusChip 
                      label={conversation.status || 'completed'}
                      status={conversation.status === 'completed' ? 'active' : 'inactive'}
                      size="small"
                      aria-label={`Status: ${conversation.status || 'completed'}`}
                    />
                  </ListItem>
                  {index < conversations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <WalteroButton 
            variant="secondary" 
            onClick={() => {
              startNewConversation();
              setShowConversations(false);
            }} 
            startIcon={<AddIcon />}
            sx={{ minHeight: `${touch.minTouchTarget}px` }}
            aria-label="Start new conversation"
            size={touch.buttonSize}
          >
            NEW CONVERSATION
          </WalteroButton>
          <WalteroButton 
            variant="outlined" 
            onClick={() => setShowConversations(false)}
            sx={{ minHeight: `${touch.minTouchTarget}px` }}
            aria-label="Close conversations dialog"
            size={touch.buttonSize}
          >
            CLOSE
          </WalteroButton>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog
        open={showInfo}
        onClose={() => setShowInfo(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="info-title"
        aria-describedby="info-description"
        ref={infoFocus.containerRef}
        onKeyDown={infoFocus.handleKeyDown}
      >
        <DialogTitle id="info-title">AI Assistant Information</DialogTitle>
        <DialogContent id="info-description">
          <WalteroTypography variant="h6" gutterBottom component="h2">Service Status</WalteroTypography>
          {healthStatus ? (
            <Box>
              <WalteroStatusChip 
                label={healthStatus.status}
                status={healthStatus.status === 'OK' ? 'online' : 'error'}
                sx={{ mb: 1 }}
              />
              <WalteroTypography variant="body2" sx={{ mb: 2 }}>
                {healthStatus.message || 'Service is running normally'}
              </WalteroTypography>
              {healthStatus.features && (
                <Box>
                  <WalteroTypography variant="subtitle2" gutterBottom>Available Features:</WalteroTypography>
                  {Object.entries(healthStatus.features).map(([feature, status]) => (
                    <WalteroStatusChip
                      key={feature}
                      label={`${feature}: ${status}`}
                      status="info"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <WalteroTypography>Loading status...</WalteroTypography>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <WalteroTypography variant="h6" gutterBottom>Capabilities</WalteroTypography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Device Lookup" 
                secondary="Search for devices by serial numbers with organization access control"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Sensor Telemetry" 
                secondary="Query time-series data for 40+ sensor measures from TimeStream database"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Device Configuration" 
                secondary="Check device shadow properties including WiFi, cellular, and sensor settings"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Natural Language" 
                secondary="Ask questions in plain English about your IoT infrastructure"
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <WalteroButton 
            variant="outlined" 
            onClick={() => setShowInfo(false)}
            sx={{ minHeight: `${touch.minTouchTarget}px` }}
            aria-label="Close dialog"
            size={touch.buttonSize}
          >
            CLOSE
          </WalteroButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentTab; 